import React, { useState, useEffect, useCallback, useRef } from 'react';
import Note from './Note';
import NoteModal from './NoteModal';
import './ContentArea.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { addNote, updateNote, deleteNote, updateNoteOrder } from '../firebase/notes';
import { auth } from '../firebase';
import { useEncryption } from '../context/EncryptionContext';

const MIN_NOTE_WIDTH = 300;
const MAX_NOTES_PER_ROW = 4;
const NOTES_ROW_GAP = 26;
const GENERIC_SAVE_ERROR = 'Unable to save note. Please try again.';

export const getNotesPerRow = (containerWidth) => Math.min(
  MAX_NOTES_PER_ROW,
  Math.max(1, Math.floor((containerWidth + NOTES_ROW_GAP) / (MIN_NOTE_WIDTH + NOTES_ROW_GAP)))
);

// react-beautiful-dnd predates React 18's development-only Strict Mode remount.
// Delaying the initial Droppable mount by one frame keeps its registry intact
// without disabling Strict Mode for the rest of the app.
export const StrictModeDroppable = ({ children, ...droppableProps }) => {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => setIsEnabled(true));
    return () => cancelAnimationFrame(frameId);
  }, []);

  if (!isEnabled) return null;

  return <Droppable {...droppableProps}>{children}</Droppable>;
};

const ContentArea = ({ 
  createNoteTrigger, 
  setCreateNoteTrigger, 
  selectedCategory, 
  searchQuery, 
  searchFilter = 'title',
  categories, 
  notes, 
  setNotes, 
  selectedTag, 
  tags,
  selectedNote,
  setSelectedNote,
  onExport,
  onClearFilters
}) => {
  const [error, setError] = useState(null);
  const [notesPerRow, setNotesPerRow] = useState(MAX_NOTES_PER_ROW);
  const notesContainerRef = useRef(null);
  const { isEncryptionLocked } = useEncryption();
  const lockMessage = 'Note changes are temporarily unavailable while encryption is being verified or changed.';
  const blockMutation = () => {
    setError(lockMessage);
    return true;
  };

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('Error:', error);
    }
  }, [error]);

  // Memoize createNote function
  const createNote = useCallback(async () => {
    if (isEncryptionLocked) { blockMutation(); return; }
    const user = auth.currentUser;
    if (!user) {
      setError('Please sign in to create notes');
      return;
    }

    // Find the highest order value
    const maxOrder = notes.reduce((max, note) => Math.max(max, note.order || 0), 0);

    const newNote = {
      userId: user.uid,
      title: '',
      content: '',
      color: '#ADD8E6',
      category: 'Uncategorized',
      tags: [],
      isArchived: false,
      pinned: false,
      order: maxOrder + 1000 // Place new note at the end
    };
    setSelectedNote(newNote);
  }, [notes, setError, setSelectedNote, isEncryptionLocked]);

  // Handle the create note trigger
  useEffect(() => {
    if (createNoteTrigger) {
      createNote();
      setCreateNoteTrigger(false); // Reset the trigger
    }
  }, [createNoteTrigger, setCreateNoteTrigger, createNote]);

  useEffect(() => {
    const notesContainer = notesContainerRef.current;
    if (!notesContainer) return undefined;

    const updateNotesPerRow = (width) => {
      if (width > 0) setNotesPerRow(getNotesPerRow(width));
    };

    updateNotesPerRow(notesContainer.clientWidth);

    if (typeof ResizeObserver === 'undefined') {
      const handleWindowResize = () => updateNotesPerRow(notesContainer.clientWidth);
      window.addEventListener('resize', handleWindowResize);
      return () => window.removeEventListener('resize', handleWindowResize);
    }

    const observer = new ResizeObserver(([entry]) => updateNotesPerRow(entry.contentRect.width));
    observer.observe(notesContainer);
    return () => observer.disconnect();
  }, []);

  // Split notes into rows
  const getNotesInRows = (notes) => {
    const rows = [];
    for (let i = 0; i < notes.length; i += notesPerRow) {
      rows.push(notes.slice(i, i + notesPerRow));
    }
    return rows;
  };

  const onDragEnd = async (result) => {
    if (isEncryptionLocked) { blockMutation(); return; }
    const { source, destination } = result;
    
    if (!destination) return;

    try {
      const allNotes = Array.from(notes);
      const filteredNotesArray = Array.from(filteredNotes);

      // Find the actual note being moved
      const sourceRowIndex = parseInt(source.droppableId.split('-')[1]);
      const destRowIndex = parseInt(destination.droppableId.split('-')[1]);

      console.log('Drag operation:', {
        sourceRow: sourceRowIndex,
        destRow: destRowIndex,
        sourceIndex: source.index,
        destIndex: destination.index
      });

      // Get the actual indices
      const sourceIndex = sourceRowIndex * notesPerRow + source.index;
      const destIndex = destRowIndex * notesPerRow + destination.index;
      
      // Get the actual note being moved
      const sourceNote = filteredNotesArray[sourceIndex];
      
      // Sort notes by order for accurate positioning
      const sortedNotes = [...filteredNotesArray].sort((a, b) => a.order - b.order);
      
      // Find the notes before and after the destination position
      let prevNote = null;
      let nextNote = null;
      let newOrder;
      
      if (destIndex === 0) {
        nextNote = sortedNotes[0];
        newOrder = nextNote.order - 1000;
      } else if (destIndex >= filteredNotesArray.length) {
        prevNote = sortedNotes[sortedNotes.length - 1];
        newOrder = prevNote.order + 1000;
      } else {
        // Find the actual notes that will be before and after the moved note
        const notesWithoutSource = sortedNotes.filter(note => note.id !== sourceNote.id);
        prevNote = notesWithoutSource[destIndex - 1];
        nextNote = notesWithoutSource[destIndex];
        
        if (prevNote && nextNote) {
          newOrder = (prevNote.order + nextNote.order) / 2;
        } else if (prevNote) {
          newOrder = prevNote.order + 1000;
        } else if (nextNote) {
          newOrder = nextNote.order - 1000;
        }
      }

      console.log('Order calculation:', {
        oldOrder: sourceNote?.order,
        newOrder: newOrder,
        prevNoteOrder: prevNote?.order,
        nextNoteOrder: nextNote?.order
      });

      // Update the note order in Firebase
      await updateNoteOrder(sourceNote.id, newOrder);
      
      // Update local state
      const updatedNotes = allNotes.map(note => 
        note.id === sourceNote.id ? { ...note, order: newOrder } : note
      );
      
      setNotes(updatedNotes);
    } catch (error) {
      console.error('Error reordering notes:', error);
      setError('Failed to reorder notes. Please try again.');
    }
  };

  const saveNoteContent = async (newTitle, newContent, newCategory, newTags, protection) => {
    if (isEncryptionLocked) {
      blockMutation();
      throw new Error(lockMessage);
    }
    try {
      const user = auth.currentUser;
      if (!user) {
        const saveError = new Error('Please sign in to save notes');
        setError(GENERIC_SAVE_ERROR);
        throw saveError;
      }

      const noteData = {
        userId: user.uid,
        title: newTitle || new Date().toLocaleDateString(),
        content: newContent,
        category: newCategory,
        tags: newTags,
        isArchived: selectedNote.isArchived || false,
        pinned: selectedNote.pinned || false,
        order: selectedNote.order || (notes.length > 0 ? Math.max(...notes.map(n => n.order || 0)) + 1000 : 1000)
      };

      if (selectedNote.id && (protection === 'standard' || selectedNote.protection === 'standard')) noteData.protection = 'standard';

      if (selectedNote.id) {
        // Update existing note
        await updateNote(selectedNote.id, noteData);
      } else {
        // Add new note
        await addNote(noteData);
      }
      setSelectedNote(null);
    } catch (err) {
      console.error('Error saving note:', err);
      setError(GENERIC_SAVE_ERROR);
      throw err;
    }
  };

  const deleteNoteHandler = async (id) => {
    if (isEncryptionLocked) { blockMutation(); return; }
    try {
      await deleteNote(id);
    } catch (err) {
      setError(err.message);
    }
  };

  const duplicateNoteHandler = async (id) => {
    if (isEncryptionLocked) { blockMutation(); return; }
    try {
      const originalNote = notes.find(note => note.id === id);
      if (originalNote) {
        const user = auth.currentUser;
        if (!user) {
          setError('Please sign in to duplicate notes');
          return;
        }

        const duplicateData = {
          ...originalNote,
          userId: user.uid,
          title: `${originalNote.title} (Copy)`,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        delete duplicateData.id;
        await addNote(duplicateData);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const archiveNoteHandler = async (id, archiveStatus) => {
    if (isEncryptionLocked) { blockMutation(); return; }
    try {
      const noteToUpdate = notes.find(note => note.id === id);
      if (noteToUpdate) {
        await updateNote(id, { ...noteToUpdate, isArchived: archiveStatus });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const pinNoteHandler = async (id, pinStatus) => {
    if (isEncryptionLocked) { blockMutation(); return; }
    try {
      const noteToUpdate = notes.find(note => note.id === id);
      if (noteToUpdate) {
        await updateNote(id, { ...noteToUpdate, pinned: pinStatus });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getCategoryColor = (categoryName) => {
    const category = categories.find(cat => cat.name === categoryName);
    return category ? category.color : 'bg-gray-300';
  };

  // Filter notes
  const filteredNotes = notes
    .filter(note => {
      // Debug selected category first
      console.log('Current selected category:', selectedCategory);
      console.log('All notes:', notes.length);

      // More explicit category matching logic
      let matchesCategory = false;
      
      if (selectedCategory === 'All Notes') {
        matchesCategory = !note.isArchived;
      } else if (selectedCategory === 'Archived') {
        matchesCategory = note.isArchived;
      } else {
        matchesCategory = note.category === selectedCategory && !note.isArchived;
      }

      const normalizedQuery = searchQuery.trim().toLowerCase();
      const searchableMetadata = {
        title: note.title,
        tags: (note.tags || []).join(' '),
        category: note.category,
      };
      const matchesSearch = !normalizedQuery ||
        searchableMetadata[searchFilter]?.toLowerCase().includes(normalizedQuery);
      const matchesTags = !selectedTag?.length || 
                         (note.tags && selectedTag.every(tag => note.tags.includes(tag)));

      return matchesCategory && matchesSearch && matchesTags;
    })
    .sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (a.order || 0) - (b.order || 0);
    });

  const hasActiveFilters = Boolean(
    searchQuery.trim() || selectedCategory !== 'All Notes' || selectedTag?.length
  );
  const activeFilterLabels = [
    selectedCategory !== 'All Notes' ? `Category: ${selectedCategory}` : null,
    selectedTag?.length ? `Tags: ${selectedTag.join(', ')}` : null,
    searchQuery.trim() ? `Search: ${searchFilter}` : null,
  ].filter(Boolean);
  const resultLabel = `${filteredNotes.length} ${filteredNotes.length === 1 ? 'note' : 'notes'}`;

  // Log filtered result count without logging note data.
  React.useEffect(() => {
    console.log("Final filtered notes:", filteredNotes.length);
  }, [filteredNotes]);

  const handleAddTag = async (noteId, newTag) => {
    if (isEncryptionLocked) { blockMutation(); return; }
    try {
      const noteToUpdate = notes.find(note => note.id === noteId);
      if (noteToUpdate) {
        const updatedTags = [...(noteToUpdate.tags || [])];
        if (!updatedTags.includes(newTag)) {
          updatedTags.push(newTag);
          await updateNote(noteId, { ...noteToUpdate, tags: updatedTags });
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="content-area bg-transparent p-6">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        )}
        {isEncryptionLocked && <div role="status" className="mb-4 text-sm text-gray-700 dark:text-gray-300">{lockMessage}</div>}
        <div className="notes-container" ref={notesContainerRef}>
            <p role="status" aria-live="polite" className="mb-3 text-sm text-gray-600 dark:text-gray-300">
              {resultLabel}{activeFilterLabels.length ? ` · ${activeFilterLabels.join(' · ')}` : ''}
            </p>
            {notes.length === 0 ? (
              <div className="py-12 text-center text-gray-700 dark:text-gray-300">
                <h2 className="text-xl font-semibold">Start with your first note</h2>
                <p className="mt-2">Capture an idea, task, or reference and organize it later.</p>
                <button
                  type="button"
                  onClick={createNote}
                  disabled={isEncryptionLocked}
                  className="mt-4 rounded bg-blue-500 px-4 py-2 font-semibold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Create your first note
                </button>
              </div>
            ) : filteredNotes.length === 0 ? (
              <div className="py-12 text-center text-gray-700 dark:text-gray-300">
                <h2 className="text-xl font-semibold">{hasActiveFilters ? 'No notes match your filters.' : 'No notes in this view.'}</h2>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="mt-4 rounded border border-gray-400 px-4 py-2 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
            {getNotesInRows(filteredNotes).map((rowNotes, rowIndex) => (
              <StrictModeDroppable
                key={`row-${rowIndex}`}
                droppableId={`row-${rowIndex}`}
                direction="horizontal"
                type="NOTE"
                isDropDisabled={isEncryptionLocked}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`notes-row ${snapshot.isDraggingOver ? 'drop-target' : ''}`}
                    data-row-index={rowIndex}
                    style={{ '--note-card-width': `calc((100% - ${(notesPerRow - 1) * NOTES_ROW_GAP}px) / ${notesPerRow})` }}
                  >
                    {rowNotes.map((note, index) => (
                      <Draggable
                        key={note.id}
                        draggableId={note.id}
                        index={index}
                        isDragDisabled={isEncryptionLocked}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={() => setSelectedNote(note)}
                            className="note-wrapper"
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.5 : 1,
                              transform: snapshot.isDragging 
                                ? `${provided.draggableProps.style.transform} rotate(2deg)`
                                : provided.draggableProps.style.transform
                            }}
                          >
                            <Note
                              title={note.title}
                              color={getCategoryColor(note.category)}
                              content={note.content}
                              protection={note.protection}
                              tags={note.tags}
                              isArchived={note.isArchived}
                              isPinned={note.pinned}
                              onDelete={() => deleteNoteHandler(note.id)}
                              onArchive={() => archiveNoteHandler(note.id, !note.isArchived)}
                              onPin={() => pinNoteHandler(note.id, !note.pinned)}
                              onDuplicate={() => duplicateNoteHandler(note.id)}
                              onTagAdd={(tag) => handleAddTag(note.id, tag)}
                              createdAt={note.createdAt}
                              updatedAt={note.updatedAt}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
            ))}
              </>
            )}
        </div>
      </div>
      {selectedNote && (
        <NoteModal
          isOpen={!!selectedNote}
          onRequestClose={() => setSelectedNote(null)}
          title={selectedNote.title}
          content={selectedNote.content}
          onSave={saveNoteContent}
          categories={categories}
          selectedCategory={selectedNote.category}
          tags={selectedNote.tags || []}
          availableTags={tags}
          onExport={onExport}
          noteId={selectedNote.id}
          note={selectedNote}
          onVaultTransitionComplete={(result) => {
            if (!result?.stale) setSelectedNote(null);
          }}
          createdAt={selectedNote.createdAt}
          updatedAt={selectedNote.updatedAt}
        />
      )}
    </DragDropContext>
  );
};

export default ContentArea;
